import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { Response } from '../../interfaces/interface-bakend/response';
import { NotificacionDto } from './dto/notificacion.dto';
import { NotificacionAsuntoDto } from './dto/notificacion-asunto.dto';

export interface NotificacionHistorialDto {
  destinatarios: {
    _id: string;
    email: string;
    nombrePersona: string;
    apellidoPersona: string;
    fullName: string;
  }[];
  adjuntos: string[];
  enviado: boolean;
  fechaEnvio: string;
  masivo: boolean;
  _id: string;
  asunto: string;
  cuerpo: string;
  createdAt: string;
  updatedAt: string;
}

const URL_BACKEND = environment.urlBackend;

@Injectable({
  providedIn: 'root'
})
export class NotificacionesService {

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService
  ) { }

  // Crea una notificación; se espera enviar los datos necesarios en el body.
  createNotificacion(notificacion: NotificacionDto): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();

        // Creamos un payload a partir del DTO que asegure que:
        // - destinatarios es un arreglo.
        // - fechaEnvio sea una instancia de Date.
        // Desestructuramos 'fechaEnvio' y capturamos el resto de las propiedades en 'rest'
        const { fechaEnvio, ...rest } = notificacion;

        // Ahora construimos el payload sin 'fechaEnvio'
        const payload: NotificacionDto = {
          ...rest,
          destinatarios: Array.isArray(notificacion.destinatarios)
            ? notificacion.destinatarios
            : [notificacion.destinatarios]
        };


        // Opcional: Si deseas asegurarte de enviar JSON correctamente
        const headersWithContent = headers.set('Content-Type', 'application/json');

        this.httpClient.post<Response>(`${URL_BACKEND}/notificaciones`, payload, { headers: headersWithContent })
          .subscribe(resp => {
            // Se asume que la respuesta contiene el ID de la notificación creada
            console.log('RESPUESTA', resp);
            resolve(resp.response._id);
          }, (error: HttpErrorResponse) => {
            console.error('Error creando notificación:', error);
            reject(error);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  // Envía una notificación creada, usando su _id.
  sendNotificacion(id: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.post<Response>(`${URL_BACKEND}/notificaciones/enviar/${id}`, {}, { headers })
          .subscribe(resp => {
            resolve(true);
          }, error => {
            console.error('Error enviando notificación:', error);
            reject(false);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  // Métodos para asuntos de notificación
  getAsuntosActivos(): Promise<NotificacionAsuntoDto[]> {
    return new Promise(async resolve => {
      const headers = await this.authService.getHeadersBearer();
      this.httpClient.get<Response>(`${URL_BACKEND}/notificacion-asuntos/activos`, { headers })
        .subscribe(resp => {
          resolve(resp.response);
        });
    });
  }

  addAsunto(asunto: NotificacionAsuntoDto): Promise<NotificacionAsuntoDto> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.post<Response>(`${URL_BACKEND}/notificacion-asuntos`, asunto, { headers })
          .subscribe(resp => {
            resolve(resp.response);
          }, error => {
            console.error('Error agregando asunto:', error);
            reject(error);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  getMisMensajes(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/mis-mensajes`, { headers })
          .subscribe(resp => {
            const list = resp?.response ?? resp ?? [];
            resolve(list);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getNoLeidosCount(): Promise<number> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/mis-mensajes/no-leidos/count`, { headers })
          .subscribe(resp => {
            const val = resp?.response?.count ?? resp?.count ?? 0;
            resolve(val);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  marcarComoLeidaSegura(notificacionId: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.put<any>(`${URL_BACKEND}/notificaciones/mis-mensajes/${notificacionId}/leida`, {}, { headers })
          .subscribe(() => resolve(), error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getDestinatariosDetalle(id: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/destinatarios-detalle/${id}`, { headers })
          .subscribe(resp => {
            const list = resp?.response ?? resp ?? [];
            resolve(list);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  getDetalleMetricas(id: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.get<any>(`${URL_BACKEND}/notificaciones/detalle-metricas/${id}`, { headers })
          .subscribe(resp => {
            const detail = resp?.response ?? resp ?? {};
            resolve(detail);
          }, error => reject(error));
      } catch (e) {
        reject(e);
      }
    });
  }

  deleteAsunto(asuntoId: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await this.authService.getHeadersBearer();
        this.httpClient.delete<Response>(`${URL_BACKEND}/notificacion-asuntos/${asuntoId}`, { headers })
          .subscribe(resp => {
            resolve(true);
          }, error => {
            console.error('Error eliminando asunto:', error);
            reject(false);
          });
      } catch (err) {
        reject(err);
      }
    });
  }

getHistorial(desde?: Date, hasta?: Date): Promise<NotificacionHistorialDto[]> {
  return new Promise(async (resolve, reject) => {
    try {
      const headers: HttpHeaders = await this.authService.getHeadersBearer();
      let url = `${URL_BACKEND}/notificaciones/historial`;
      // Si deseas seguir pasando los parámetros por URL (opcional)
      const params: string[] = [];
      if (desde) {
        params.push(`desde=${desde.toISOString()}`);
      }
      if (hasta) {
        params.push(`hasta=${hasta.toISOString()}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      this.httpClient.get<{ response: NotificacionHistorialDto[] }>(url, { headers }).subscribe(
        resp => {
          console.log('NotificacionHistorialDto',  resp.response);

          let resultado = resp.response;

          // Si las fechas son pasadas, se filtrará localmente
          console.log();
          if (desde) {
            resultado = resultado.filter(item => new Date(item.fechaEnvio) >= desde);
          }
          if (hasta) {
            resultado = resultado.filter(item => new Date(item.fechaEnvio) <= hasta);
          }
          console.log('resultaod', resultado);
          resolve(resultado);
        },
        error => reject(error)
      );
    } catch (e) {
      reject(e);
    }
  });
}

}


